import React, { memo, useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import { bindTrigger, bindPopover, usePopupState } from 'material-ui-popup-state/hooks';
import { ArrowRightAlt, Close, Edit, FiberManualRecord, Help, HotTub, OpenWith, PlusOneRounded, PushPin, Star, ExpandMore, ExpandLess } from '@mui/icons-material';
import { areClassNamePropsEqual, filterData, getFields } from '../utilities/utils';
import { IconButton, InputAdornment, Paper, TextField, Collapse, Divider, List, ListItem, MenuItem, ListItemText } from '@mui/material';
import { orange, yellow } from '@mui/material/colors';
import { IHelpTopic, ISystemConfig } from '../ts/Definitions';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import { navigateToPage } from '../controls/ProtectedPage';
import debounce from 'lodash/debounce';
import { DebouncedFunc } from '../ts/Definitions';
import { ControllerContext } from '../ts/Contexts';
import { unpackEventTarget } from '../utilities/renderUtils';
import { lazyWithRetry } from '../utilities/lazyLoaderUtils';


const HelpTopicEditor = lazyWithRetry(() => (import('../dialogs/HelpTopicEditor')));

interface Props {
    className?: string,
}

export const getTopicElement = (
    currentTopic: IHelpTopic | undefined,
    navigateToPage: (target: string) => void,
    topics: Record<string, IHelpTopic>,
    setCurrentTopic: (link: IHelpTopic) => void,
    showHelp: () => void): JSX.Element[] => {
    let textPart;

    const parts = currentTopic?.content.split(/#l|@g|#b|#i|@i/gm);

    if (!parts || parts.length < 2) textPart = [(<span key={Object.values(topics).indexOf(currentTopic as IHelpTopic)} className='topicContent'>{currentTopic?.content}</span>)];
    else textPart = parts.map((p, i) => {
        if (p.startsWith('ink=') && p.includes('#')) {
            const link = p.replace('ink=', '').split('#');
            return (<span key={i}>
                <Button onClick={() => {
                    showHelp();
                    setCurrentTopic(topics[link[0]]);
                }} endIcon={<Help />}>{link[0]}</Button>
                <span className='topicContent'>{link[1]}</span>
            </span>);
        }
        if (p.startsWith('oto=') && p.includes('@')) {
            const link = p.replace('oto=', '').split('@');
            return (<span key={i}>
                <Button onClick={() => navigateToPage(link[0])} endIcon={<ArrowRightAlt />}>{link[0]}</Button>
                <span className='topicContent'>{link[1]}</span>
            </span>);
        }
        if (p.startsWith('old=') && p.includes('#')) {
            const link = p.replace('old=', '').split('#');
            return (<span key={i}>
                <span className='topicContent'><b>{link[0]}</b></span>
                <span className='topicContent'>{link[1]}</span>
            </span>);
        }
        if (p.startsWith('talic=') && p.includes('#')) {
            const link = p.replace('talic=', '').split('#');
            return (<span key={i}>
                <span className='topicContent'><i>{link[0]}</i></span>
                <span className='topicContent'>{link[1]}</span>
            </span>);
        }
        if (p.startsWith('mg=') && p.includes('#')) {
            const link = p.replace('mg=', '').split('#');
            let h = 16;
            let w = 16;
            const widthIdx = link[1].indexOf('w');
            const equalIdx = link[1].indexOf('=');
            if (widthIdx > -1 && equalIdx > -1 && link[1].startsWith('h')) {
                h = parseInt(link[1].substring(1, widthIdx));
                w = parseInt(link[1].substring(widthIdx + 1, equalIdx));
                link[1] = link[1].substring(equalIdx + 1);
            }
            return (<span key={i}>
                <img height={h} width={w} src={link[0]} />
                <span className='topicContent'>{link[1]}</span>
            </span>);
        }
        return (<span key={i} className='topicContent'>{p}</span>);
    });

    if (currentTopic?.image) {
        let images;
        if (typeof currentTopic.image === 'string') {
            images = [(
                <a href={currentTopic.image}
                    rel="noopener noreferrer"
                    target="_blank">
                    <img className='helpTopicImageItemSrc' src={currentTopic.image} alt='A help screenshot' />
                </a>)]
        } else {

            images =
                (<div className='helpImagesList'>
                    {currentTopic.image.map((i, idx) => (
                        <a href={i}
                            key={idx}
                            rel="noopener noreferrer"
                            target="_blank">
                            <img className='helpTopicImageItemSrc' src={i} alt='A help screenshot' />
                        </a>))
                    }
                </div>);
        }

        return [(<span key={`helpTopicItem-${currentTopic.title}`}>
            <div className='helpTopicImageContainer-mobile'>
                {textPart && <p className='helpTopicImageItemText'>{textPart}</p>}
                {images}
                {currentTopic.video && <video src={currentTopic.video} width={640} height={480} controls />}
            </div>
        </span>)]
    }
    return textPart;
}

let throttledFilterSave: DebouncedFunc<(value: string) => void>;

const HelpButton = ({ className }: Props) => {
    const controller = useContext(ControllerContext);
    const popupState = usePopupState({ variant: 'popover', popupId: 'help-popover', disableAutoFocus: true });

    const history = useNavigate();
    const [isPinned, setIsPinned] = useState(false);
    const [filter, setFilter] = useState('');
    const [openNodes, setOpenNodes] = useState<string[]>([]);
    const [filterField, setFilterField] = useState<string>('');
    const [newTopicName, setNewTopicName] = useState<string>('');
    const [topics, setTopics] = useState<Record<string, IHelpTopic>>({});
    const [hotTopics, setHotTopics] = useState<string[]>([]);
    const [currentTopic, setCurrentTopic] = useState<IHelpTopic | undefined>();
    const [showTopicEditor, setShowTopicEditor] = useState<{ item?: IHelpTopic, topicLink: string }>();
    const popupContainerState = usePopupState({ variant: 'popover', popupId: 'helpTopic-popover', disableAutoFocus: true });
    const [anchorEl, setAnchorEl] = React.useState<HTMLSpanElement | null>(null);

    useEffect(() => {
        if (!controller) return;
        void controller.getConfig().subscribe(refresh);
        return () => {
            controller.getConfig().unsubscribe(refresh);
        }
    }, [controller]);

    useEffect(() => {
        throttledFilterSave = debounce((value: string) => setFilter(value), 100);
    }, []);


    const gotoPage = (target: string) => {
        navigateToPage(target, history);
        popupContainerState.close();
    };

    const currentTopicText = getTopicElement(currentTopic, gotoPage, topics, setCurrentTopic,
        () => popupState.open(popupState.anchorEl));

    const helpDetail = (
        <div>
            <Close
                className="stickyClose"
                onClick={() => {
                    setCurrentTopic(undefined);
                    if (isPinned) setIsPinned(false);
                    else popupContainerState.close();
                }}
            />
            {!isPinned && (
                <PushPin
                    className="stickyPin"
                    onClick={() => {
                        if (isPinned) {
                            popupContainerState.open(popupContainerState.anchorEl);
                            setCurrentTopic(undefined);
                            setIsPinned(false);
                        } else {
                            setIsPinned(true);
                            popupState.close();
                        }
                    }}
                />
            )}
            {isPinned && (
                <span title="Click to drag around">
                    <OpenWith className="helpDragHandle" />
                </span>
            )}
            <p>
                <h2>{currentTopic?.title}</h2>
                <p className='helpTopicContainer'>
                    {currentTopicText}
                </p>
            </p>
        </div>
    );

    const refresh = (config: ISystemConfig) => {
        if (!config) return;
        void controller.getConfig().getHotTopics().then((hTs) => {
            setHotTopics(Object.keys(hTs).sort((x, y) => hTs[y] - hTs[x]).slice(0, 3));
        });
        Object.keys(config.help).forEach(k => config.help[k].key = k);
        setTopics(config.help);
    }

    const showNewTopicDialog = (topicLink: string, topic?: IHelpTopic) => {
        setShowTopicEditor({ item: topic, topicLink });
        setNewTopicName('');
    }

    const fields = getFields<IHelpTopic>();
    const filterTopics = filterData(Object.values(topics), filter, [fields.title, fields.content]);

    const helpContainer = (
        <div className="helpWindow">
            <div className="helpTopics">

                <div className='searchBoxBanner'>
                    <TextField
                        autoFocus
                        variant="standard"
                        className={`searchBox ${filter ? 'yellow' : ''}`}
                        value={filterField}
                        fullWidth
                        onChange={event => unpackEventTarget(event, value => {
                            setFilterField(value);
                            throttledFilterSave(value);
                        })}
                        placeholder="Search Help..." />
                </div>
                {controller.getLoggedOnUserSettings().isSystemLoggedOn() && (
                    <div>
                        <TextField
                            variant="standard"
                            className='searchBox'
                            value={newTopicName}
                            fullWidth
                            onChange={event => unpackEventTarget(event, value => {
                                setNewTopicName(value);
                                setFilterField(value);
                                throttledFilterSave(value);
                            })}
                            placeholder="New Topic..."
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            disabled={newTopicName.length === 0 || topics[newTopicName] !== undefined}
                                            aria-label="Add New Help Topic"
                                            onClick={() => showNewTopicDialog(newTopicName)}
                                            size="large">
                                            <PlusOneRounded />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }} />
                    </div>
                )}
                {filterTopics && Object.entries(filterTopics.filter(t => t && t.title).sort((x, y) => {
                    if (x.isPinned && !y.isPinned) return -1;
                    if (!x.isPinned && y.isPinned) return 1;
                    return x.title?.toLowerCase().localeCompare(y.title?.toLowerCase());
                }).reduce((c, p) => {
                    if (!c) c = {};
                    if (p && p.topicGroup) (c[p.topicGroup] = c[p.topicGroup] || []).push(p);
                    else if (p) (c[''] = c[''] || []).push(p);
                    return c;
                }, {} as Record<string, IHelpTopic[]>))
                    .sort((a, b) => {
                        if (a[0] === '' && b[0] !== '') return 1;
                        if (a[0] !== '' && b[0] === '') return -1;
                        return a[0].localeCompare(b[0]);
                    }).map(([key, value]) => {
                        const items = value.map((t) => (
                            <MenuItem
                                key={t.title}
                                className='helpTopic'
                                ref={(ref) => setAnchorEl(ref)}
                                {...bindTrigger(popupState)}
                                onClick={() => {
                                    setCurrentTopic(t);
                                    controller.incrementHelpTopicCount(t.title);
                                    popupContainerState.open(anchorEl);
                                }}
                                role="presentation"
                            >
                                {controller.getLoggedOnUserSettings().isSystemLoggedOn() && (
                                    <Edit style={{ color: 'lightGray' }} onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        showNewTopicDialog(t.key as string, t);
                                    }} />
                                )}
                                <FiberManualRecord fontSize='inherit' className='helpTopicBullet' />
                                {t && t.title}
                                {hotTopics.includes(t.title) && (<span><span className='metaTag'>hot</span><HotTub style={{ color: orange[500] }} /></span>)}
                                {t.isNew && (<span><span className='metaTag'>new</span><Star style={{ color: yellow[500] }} /></span>)}
                            </MenuItem>
                        ));

                        if (!key && key.length === 0)
                            return (
                                <div key={'default'}>
                                    <Divider />
                                    {items}
                                </div>
                            );
                        return (
                            <List key={key} style={{ paddingTop: 0, paddingBottom: 0 }}>
                                <ListItem
                                    style={{ paddingTop: 0, paddingBottom: 0 }}
                                    onClick={() => {
                                        const newNodes = [...openNodes];
                                        if (openNodes.indexOf(key) > -1) newNodes.splice(openNodes.indexOf(key), 1);
                                        else newNodes.push(key);
                                        setOpenNodes(newNodes);
                                    }}
                                >
                                    <ListItemText primary={key} />
                                    {filter?.length > 0 || openNodes.indexOf(key) > -1 ? <ExpandLess /> : <ExpandMore />}
                                </ListItem>
                                <Collapse in={filter?.length > 0 || openNodes.indexOf(key) > -1} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding style={{ paddingLeft: 10 }}>
                                        {items}
                                    </List>
                                </Collapse>
                            </List>
                        );
                    })}
            </div>
        </div>
    );

    return (
        <span data-targetid="help">
            <Button
                variant="contained"
                color="primary"
                className={`${className} navActionButton`}
                title="Help topics exist for most activities on the site"
                {...bindTrigger(popupState)}>
                <span className="navButtonText">
                    <Help fontSize='medium' />
                    Help
                </span>
            </Button>
            <Popover
                {...bindPopover(popupState)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box p={2} className='helpBox'>
                    <Button
                        className='containerCloseButton black'
                        onClick={() => {
                            popupState.close();
                            setIsPinned(false);
                        }}><Close /></Button>
                    {helpContainer}
                </Box>
            </Popover>
            {currentTopic && !isPinned && (
                <Popover
                    {...bindPopover(popupContainerState)}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <div className="popupHelpContainer">
                        {helpDetail}
                    </div>
                </Popover>
            )}
            {currentTopic && isPinned && (
                <div>
                    <Draggable defaultPosition={{ x: 0, y: 0 }} handle=".helpDragHandle">
                        <Paper elevation={5} className="pinnedHelpContainer">
                            {helpDetail}
                        </Paper>
                    </Draggable>
                </div>
            )}
            {showTopicEditor && (
                <HelpTopicEditor
                    item={showTopicEditor.item}
                    topicLink={showTopicEditor.topicLink}
                    callback={() => setShowTopicEditor(undefined)}
                    availableGroups={
                        Array.from(new Set(filterTopics.filter(t => t.topicGroup).map(t => t.topicGroup))).sort((a, b) => (a as string).localeCompare(b as string)) as string[]
                    }
                />
            )}
        </span>
    );
}

export default memo(HelpButton, (prev, next) => areClassNamePropsEqual(prev, next));